// VIP Plan Progress
// Single-source-of-truth injector for a Lived Business Plan's fill
// figure inside case POST content - specifically the at-a-glance box's
// row 5 on every published case (the `data-vip-plan-progress` wrapper
// CPS v1.8.1 added there; see docs/at-a-glance-markup.md and
// docs/plan-progress-injection.md). This is pasted Ghost post content,
// not a theme template.
//
// SCOPE NARROWED (build-session-33, after build-session-31/"Spec D" had
// widened it): this script now touches ONLY that case-post element.
// Spec D extended it to also drive the dedicated plan page's continuous
// bar (partials/plan-progress.hbs) and the homepage/family-page row
// (partials/plan-teaser.hbs) - both are theme-rendered from
// partials/plan-fill.hbs and both also carry a `data-vip-plan-progress`
// attribute, so this script's old broad selector matched them too. That
// coupling was a real, live risk, not a theoretical one: this file's
// own previous header comment documented an example VIP_PLANS.masa-madre
// config with `total: 10`, and the actual live Masa Madre plan page
// (confirmed by fetching it fresh this session) already correctly
// renders "0 of 11" from plan-fill.hbs - if that stale example had ever
// been pasted into Code Injection as given, this script would have
// silently overwritten the plan page's correct eleven-segment bar with
// a wrong ten-segment one the next time anyone loaded the page. Fixed
// by removing this script's ability to touch that shape at all, not
// just by correcting the example number - a config typo should not be
// able to corrupt a page this script has no business touching.
// partials/plan-progress.hbs and partials/plan-teaser.hbs both still
// carry their own `data-vip-plan-progress="{familySlug}"` attribute
// (not edited this session - out of scope, "not this session's
// business" per the brief) but this script no longer does anything
// with elements shaped like theirs; see the shape check in render()
// below for exactly how they're recognised and skipped.
//
// SOURCE OF TRUTH: window.VIP_PLANS, keyed by family (tag slug), set
// via Ghost Admin -> Settings -> Code injection -> Site Header (see
// docs/plan-progress-injection.md). Editable there without a theme
// rebuild or redeploy.
//
//   <script>
//     window.VIP_PLANS = {
//       default: "racine",
//       racine: {
//         filled: 7, total: 10, sections: [1, 2, 5, 6, 7, 9, 10],
//         url: "/racine-business-plan/",
//         label: "Racine's Lived Business Plan"
//       },
//       "masa-madre": {
//         filled: 0, total: 11, sections: [],
//         url: "/masa-madre-business-plan/",
//         label: "Masa Madre's Lived Business Plan"
//       }
//     };
//   </script>
//
// Key convention ("masa-madre", hyphenated) matches the family tag slug
// already in ambient scope everywhere else in the theme (build-session-
// 31/32) - not a variant of it.
//
// `url` and `label` are carried in config for forward use but NOT read
// by this script - see docs/plan-progress-injection.md's "What's still
// hardcoded" section for exactly why (the at-a-glance lead line and
// section-links, and the case-routes "See the business plan" link
// text, all sit outside the data-vip-plan-progress element this script
// touches, and two of the three carry genuinely per-case content that
// can't be regenerated from a global path config anyway - re-confirmed
// against a live case page this session, not assumed from the last
// investigation). No colour key either - segments inherit
// `--family-colour` from CSS (vip.css §11), already defined for every
// family; duplicating colour into this config would be a second place
// for the same fact to drift from the first.
//
// AUTHORING CONTRACT - this script never adds the data-vip-plan-progress
// markup, only rewrites what's found, and only this one shape:
//
//   <div data-vip-plan-progress="masa-madre">
//     <div class="vip-plan-segments-track vip-plan-segments-track--micro" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="11" aria-label="Sections filled">
//       <span class="vip-plan-segment"></span>
//       ... eleven total ...
//     </div>
//     <p style="...">0 of 11 sections filled</p>
//   </div>
//
// The six live Racine posts carry a bare `<div data-vip-plan-progress>`
// (no value, predating family-scoping) - that keeps working untouched,
// via the fallback below. No case-post edits were made or are needed
// for this, this session or any prior one.
//
// FAMILY RESOLUTION, per element:
//   - Attribute has a value, and it's a key in VIP_PLANS -> use that
//     family's config.
//   - Attribute is empty/absent (the six existing posts) -> fall back
//     to VIP_PLANS.default, and log a console warning naming the
//     element. Not a silent fallback on purpose: once a second family
//     exists, "no family declared" is exactly the state a forgotten
//     attribute on a new Masa Madre post would also be in, and that
//     failure mode (quietly showing Racine's numbers under a Masa
//     Madre box) is worse than a harmless console warning on every
//     legacy post that hasn't been given an explicit family yet.
//   - Attribute has a value that ISN'T a key in VIP_PLANS -> leave the
//     element untouched (no fallback to default - an explicitly wrong
//     or not-yet-configured family is a real mistake worth surfacing,
//     not something to paper over with Racine's numbers) and log a
//     warning naming the element.
//
// FAIL-SAFE: if window.VIP_PLANS is missing entirely or malformed, or
// this script fails for any reason, every data-vip-plan-progress
// element is left exactly as pasted, with no console warnings (nothing
// is configured yet - that's the expected pre-setup state, not a
// mistake worth flagging). Once VIP_PLANS exists, a specific element's
// resolution failure (unknown family, or default itself missing/
// invalid) DOES warn, per above. Either way, no page fetch, no API
// call - a plan page can be member-gated and the reader this figure is
// for is exactly the logged-out one a fetch would fail or gate on.

(function() {
    'use strict';

    function isValidConfig(cfg) {
        return !!cfg &&
            typeof cfg.filled === 'number' && isFinite(cfg.filled) &&
            typeof cfg.total === 'number' && isFinite(cfg.total) &&
            cfg.total > 0 &&
            cfg.filled >= 0 && cfg.filled <= cfg.total;
    }

    function buildSegments(cfg) {
        var filledSet = null;
        if (Array.isArray(cfg.sections)) {
            filledSet = {};
            cfg.sections.forEach(function(n) { filledSet[n] = true; });
        }
        var frag = document.createDocumentFragment();
        for (var i = 1; i <= cfg.total; i++) {
            var span = document.createElement('span');
            // With an explicit `sections` list, mark exactly those
            // positions filled - matches the theme's own
            // partials/plan-fill.hbs convention (specific sections, not
            // just a count). Without one, fall back to filling the
            // first N of total, still correct in count if not position.
            var isFilled = filledSet ? !!filledSet[i] : (i <= cfg.filled);
            span.className = isFilled ? 'vip-plan-segment vip-plan-segment--filled' : 'vip-plan-segment';
            frag.appendChild(span);
        }
        return frag;
    }

    // The ONLY shape this script rewrites: a case-post at-a-glance box's
    // micro-bar + count paragraph, both direct children of the
    // data-vip-plan-progress element itself. The dedicated plan page's
    // continuous bar (.vip-plan-progress-bar) and the homepage/family
    // row's segment track (.vip-family-plan-segments-track) both use
    // different classes entirely and were never matched by the
    // `.vip-plan-segments-track` lookup below even before this session
    // - .vip-plan-segments-track--micro is specific to this one shape.
    // The null-check bails out and leaves those elements completely
    // untouched, which is the deliberate, sufficient guard against ever
    // touching them again - not an incidental side effect.
    function render(el, cfg) {
        var track = el.querySelector('.vip-plan-segments-track');
        var countText = el.querySelector('p');
        if (!track || !countText) {
            // Shape doesn't match what this script knows how to rewrite
            // - leave it exactly as authored/rendered rather than guess.
            // This is also how the plan page's own bar and the homepage
            // row are protected: neither has a .vip-plan-segments-track
            // descendant, so both hit this branch and are left alone.
            return;
        }
        track.setAttribute('aria-valuenow', String(cfg.filled));
        track.setAttribute('aria-valuemax', String(cfg.total));
        while (track.firstChild) {
            track.removeChild(track.firstChild);
        }
        track.appendChild(buildSegments(cfg));
        countText.textContent = cfg.filled + ' of ' + cfg.total + ' sections filled';
    }

    // Resolves which family config, if any, applies to one element.
    // Returns { cfg, warn } where `warn`, if set, is the message to log
    // (the caller attaches the element itself as a second console arg,
    // matching this script's existing warning style) - kept separate
    // from `console.warn` here so this stays a pure function to test.
    function resolvePlan(plans, el) {
        var rawFamily = el.getAttribute('data-vip-plan-progress');
        var family = rawFamily ? rawFamily.trim() : '';

        if (!family) {
            var defaultKey = plans.default;
            var defaultCfg = defaultKey ? plans[defaultKey] : null;
            if (!isValidConfig(defaultCfg)) {
                return { cfg: null, warn: 'no family declared, and the default family ("' + defaultKey + '") is missing or malformed' };
            }
            return { cfg: defaultCfg, warn: 'no family declared - falling back to default family "' + defaultKey + '"' };
        }

        if (!Object.prototype.hasOwnProperty.call(plans, family) || !isValidConfig(plans[family])) {
            return { cfg: null, warn: 'unknown or malformed family "' + family + '" - leaving box as authored' };
        }

        return { cfg: plans[family], warn: null };
    }

    function init() {
        try {
            var plans = window.VIP_PLANS;
            if (!plans || typeof plans !== 'object') {
                // Not configured yet at all - the expected state before
                // Vincent adds the code-injection snippet. Every box's
                // pasted fallback stands; no warnings, nothing to report.
                return;
            }
            var nodes = document.querySelectorAll('[data-vip-plan-progress]');
            Array.prototype.forEach.call(nodes, function(el) {
                try {
                    var resolved = resolvePlan(plans, el);
                    if (resolved.warn) {
                        console.warn('[vip-plan-progress] ' + resolved.warn + ':', el);
                    }
                    if (!resolved.cfg) {
                        return;
                    }
                    render(el, resolved.cfg);
                } catch (e) {
                    console.warn('[vip-plan-progress] failed to update, leaving as authored:', e, el);
                }
            });
        } catch (e) {
            console.warn('[vip-plan-progress] top-level init failed, boxes left as authored:', e);
        }
    }

    // Bootstrapping wrapped per CLAUDE.md §7: all theme JS concatenates
    // into one casper.js, so this guards against this component's own
    // setup throwing and taking down anything concatenated after it.
    try {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
        } else {
            init();
        }
    } catch (e) {
        console.warn('[vip-plan-progress] failed to bootstrap:', e);
    }
})();
